import { api } from "../lib/api";

export interface UserDetails {
	id: string;
	userId: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	phoneNumber?: string;
	gender?: string;
	relationshipStatus?: string;
	dateOfBirth?: string;
	dayOfBirth?: number;
	monthOfBirth?: number;
	yearOfBirth?: number;
	whatsappConsent?: boolean;
	emailConsent?: boolean;
	modifiedAt: string;
	isOnboarded: boolean;
	fertilityJourneyType?: string;
	fertilityJourneyTypes?: string[];
	fertilityJourneyProgress?: Record<string, unknown>[];
	interestedIn?: string[];
	fertilityStatus?: string;
	fertilityRole?: string;
	languagePreference?: string;
	displayLanguage?: string;
	countryCity?: string;
	additionalInformation?: string;
	additionalInformationAudioUrl?: string;
	phoneNumberVerifiedAt?: string;
	userType?: "b2c" | "b2b" | "partner" | "d2c";
	hasClinicProvider?: boolean;
	providerName?: string;
	timezone?: string;
	// The user-details payload is an open bag (extra server fields ride along);
	// this also makes it assignable to the user-data-context UserDetails, which
	// carries the same index signature.
	[key: string]: unknown;
}

export interface User {
	id: string;
	email: string;
	password?: string;
	displayName?: string;
	userImage?: string;
	resetToken?: string;
	createdAt: string;
	resetTokenExpires?: string;
	lastTwoFactorAuthAt?: string;
	auth0Id?: string;
	role: "admin" | "member";
	status: "active" | "inactive" | "deleted";
}

export interface UserWithDetails extends User {
	details: UserDetails | null;
}

interface UserApiResponse {
	user: User;
	details: UserDetails | null;
	tokenClaims: Record<string, unknown>;
}

export class UserService {
	async getUserWithDetails(): Promise<UserWithDetails> {
		try {
			const response = await api.get<UserApiResponse>("/api/user");

			// Transform API response to match our expected structure
			return {
				...response.data.user,
				details: response.data.details,
			};
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { error?: string } };
				};
				if (axiosError.response?.data?.error) {
					throw new Error(axiosError.response.data.error);
				}
			}
			throw new Error("Failed to fetch user details");
		}
	}
}

export const userService = new UserService();

/**
 * Get current user data for onboarding (returns UserDetails directly)
 */
export async function getCurrentUser(): Promise<UserDetails | null> {
	try {
		const response = await api.get<UserApiResponse>("/api/user");
		return response.data.details;
	} catch (error: unknown) {
		if (error && typeof error === "object" && "response" in error) {
			const axiosError = error as {
				response?: { data?: { error?: string } };
			};
			if (axiosError.response?.data?.error) {
				throw new Error(axiosError.response.data.error);
			}
		}
		throw new Error("Failed to fetch user details");
	}
}
